import { useEffect, useMemo, useState } from 'react';

const LEASE_TTL_MS = 12_000;
const RENEW_INTERVAL_MS = 4_000;
const LEADER_CHANNEL_NAME = 'ov-rte-leader-election';

interface Lease {
  tabId: string;
  expiresAt: number;
}

type LeaderMessage =
  | { type: 'lease'; scope: string; tabId: string; expiresAt: number }
  | { type: 'release'; scope: string; tabId: string };

interface LeaseContext {
  storageKey: string;
  tabId: string;
  channel: BroadcastChannel;
  scope: string;
}

export function useBroadcastChannelLeaderTab(scope: string, enabled = true): boolean {
  const [isLeader, setIsLeader] = useState(false);
  const tabId = useMemo(createTabId, []);

  useEffect(() => {
    if (!enabled || !scope) {
      setIsLeader(false);
      return;
    }

    if (!hasCoordinationApis()) {
      setIsLeader(true);
      return;
    }

    let channel: BroadcastChannel;
    try {
      channel = new BroadcastChannel(LEADER_CHANNEL_NAME);
    } catch {
      setIsLeader(true);
      return;
    }

    const ctx: LeaseContext = {
      storageKey: `ov-rte-leader:${scope}`,
      tabId,
      channel,
      scope,
    };

    const refreshLeadership = (): void => {
      try {
        if (document.visibilityState !== 'visible') {
          releaseLeadership(ctx, setIsLeader);
          return;
        }

        setIsLeader(claimOrRenewLeadership(ctx, Date.now()));
      } catch {
        setIsLeader(true);
      }
    };

    const onStorage = (e: StorageEvent): void => {
      if (e.key === ctx.storageKey) refreshLeadership();
    };

    const onBroadcastMessage = (e: MessageEvent<LeaderMessage>): void => {
      const msg = e.data;
      if (!msg || msg.scope !== scope || msg.tabId === tabId) return;
      if (msg.type === 'lease' || msg.type === 'release') refreshLeadership();
    };

    const onPageHide = (): void => {
      releaseLeadership(ctx, setIsLeader);
    };

    refreshLeadership();
    const renewInterval = window.setInterval(refreshLeadership, RENEW_INTERVAL_MS);

    window.addEventListener('storage', onStorage);
    window.addEventListener('pagehide', onPageHide);
    document.addEventListener('visibilitychange', refreshLeadership);
    channel.addEventListener('message', onBroadcastMessage);

    return () => {
      window.clearInterval(renewInterval);
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('pagehide', onPageHide);
      document.removeEventListener('visibilitychange', refreshLeadership);
      channel.removeEventListener('message', onBroadcastMessage);
      releaseLeadership(ctx, setIsLeader);
      channel.close();
    };
  }, [enabled, scope, tabId]);

  return isLeader;
}

function claimOrRenewLeadership(ctx: LeaseContext, now: number): boolean {
  const current = readLease(ctx.storageKey);
  if (canClaimLease(current, ctx.tabId, now)) {
    writeLease(ctx, { tabId: ctx.tabId, expiresAt: now + LEASE_TTL_MS });
  }

  const renewed = readLease(ctx.storageKey);
  return isActiveLeaseOwner(renewed, ctx.tabId, now);
}

function releaseLeadership(ctx: LeaseContext, setIsLeader: (isLeader: boolean) => void): void {
  releaseLease(ctx);
  setIsLeader(false);
}

function canClaimLease(lease: Lease | null, tabId: string, now: number): boolean {
  return !lease || lease.expiresAt <= now || lease.tabId === tabId;
}

function isActiveLeaseOwner(lease: Lease | null, tabId: string, now: number): boolean {
  return !!lease && lease.tabId === tabId && lease.expiresAt > now;
}

function readLease(storageKey: string): Lease | null {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<Lease>;
    if (!parsed.tabId || typeof parsed.expiresAt !== 'number') return null;
    return { tabId: parsed.tabId, expiresAt: parsed.expiresAt };
  } catch {
    return null;
  }
}

function writeLease(ctx: LeaseContext, lease: Lease): void {
  localStorage.setItem(ctx.storageKey, JSON.stringify(lease));
  ctx.channel.postMessage(<LeaderMessage>{
    type: 'lease',
    scope: ctx.scope,
    tabId: lease.tabId,
    expiresAt: lease.expiresAt,
  });
}

function releaseLease(ctx: LeaseContext): void {
  const lease = readLease(ctx.storageKey);
  if (lease?.tabId !== ctx.tabId) return;
  localStorage.removeItem(ctx.storageKey);
  ctx.channel.postMessage(<LeaderMessage>{
    type: 'release',
    scope: ctx.scope,
    tabId: ctx.tabId,
  });
}

function createTabId(): string {
  return globalThis.crypto?.randomUUID?.() ?? `tab-${Date.now()}-${Math.random()}`;
}

function hasCoordinationApis(): boolean {
  return typeof BroadcastChannel !== 'undefined' && typeof localStorage !== 'undefined';
}
